import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import type { SupplierListRow } from "@/modules/inventory/domain/types";
import { confirm } from "@/shared/lib/confirm";
import { RowActions } from "@/shared/ui/RowActions";
import { DataTable, createSortableColumn, TanstackReactTable } from "@/shared/ui/data-table";
import { Badge } from "@/components/ui/badge";
import { EditIcon, PowerOffIcon } from "lucide-react";

type SuppliersTableProps = {
  data: SupplierListRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (row: SupplierListRow) => void;
  onDeactivate: (id: string) => void;
};

export function SuppliersTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onEdit,
  onDeactivate,
}: SuppliersTableProps) {
  const { has } = usePermissions();
  const canManage = has("inventory:purchase:manage");

  const columns: TanstackReactTable.ColumnDef<SupplierListRow>[] = [
    createSortableColumn<SupplierListRow>("code", "ລະຫັດ", {
      size: 100,
      cell: ({ row }) => row.original.code ?? "—",
    }),
    createSortableColumn<SupplierListRow>("name", "ຊື່", { size: 200 }),
    createSortableColumn<SupplierListRow>("phone", "ເບີໂທ", {
      size: 120,
      cell: ({ row }) => row.original.phone ?? "—",
    }),
    createSortableColumn<SupplierListRow>("contactName", "ຜູ້ຕິດຕໍ່", {
      size: 140,
      cell: ({ row }) => row.original.contactName ?? "—",
    }),
    {
      id: "isActive",
      header: "ສະຖານະ",
      cell: ({ row }) =>
        row.original.isActive ? (
          <Badge variant="outline">ເປີດໃຊ້</Badge>
        ) : (
          <Badge variant="secondary">ປິດ</Badge>
        ),
    },
    {
      id: "actions",
      header: "",
      size: 48,
      cell: ({ row }) =>
        canManage ? (
          <RowActions
            actions={[
              {
                label: "ແກ້ໄຂ",
                icon: <EditIcon className="size-4" />,
                onClick: () => onEdit(row.original),
              },
              ...(row.original.isActive
                ? [
                    {
                      label: "ປິດໃຊ້",
                      icon: <PowerOffIcon className="size-4" />,
                      variant: "destructive" as const,
                      onClick: async () => {
                        const ok = await confirm({
                          title: "ປິດຜູ້ສະໜອງ?",
                          description:
                            "PO ເກົ່າຍັງເບິ່ງໄດ້ ແຕ່ຈະບໍ່ເລືອກໃນ PO ໃໝ່",
                        });
                        if (ok) onDeactivate(row.original.id);
                      },
                    },
                  ]
                : []),
            ]}
          />
        ) : null,
    },
  ];

  return (
    <DataTable
      data={data}
      columns={columns}
      isLoading={isLoading}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={onPaginationChange}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={onSortingChange}
      emptyMessage="ຍັງບໍ່ມີຜູ້ສະໜອງ — ສ້າງລາຍການໃໝ່ເພື່ອເລີ່ມ"
    />
  );
}
